// Read-only grid pozycji oferty z perspektywy klienta.
// Brak edycji, brak danych kosztowych (purchase/min/margin) - to dane wewnętrzne handlowca.
// Dla pozycji z priceThresholdsAsVariants=true renderuje wybor wariantu (RadioGroup).

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

import { ISalesCrmMyOfferPosition, useGetSalesCrmMyOfferPositions } from 'api';
import ClientPositionCommentsCard from 'components/containers/ClientPositionCommentsCard';

export interface IClientOfferVariantSelection {
  positionId: number;
  qtyFrom: number;
}

interface IProps {
  offerId: string;
  selectedVariants?: Record<number, number>;
  onVariantsChange?: (variants: Record<number, number>) => void;
  onVariantPositionsResolved?: (positionIds: number[]) => void;
  readOnly?: boolean;
}

const hasVariantOptions = (p: ISalesCrmMyOfferPosition): boolean =>
  p.priceThresholdsAsVariants === true &&
  Array.isArray(p.priceThresholds) &&
  p.priceThresholds.length > 0;

const formatPrice = (raw: number | null): string => {
  if (raw == null || Number.isNaN(raw)) return '-';
  return `${raw.toFixed(2).replace('.', ',')} zł`;
};

/**
 * Skraca nazwę jednostki do najczęstszych skrótów.
 * Backend zwraca pełną polską nazwę (np. "Sztuka", "Kilogram") - chcemy "szt.", "kg".
 */
const shortenUnit = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const lower = raw.toLowerCase().trim();
  const map: Record<string, string> = {
    sztuka: 'szt.',
    sztuki: 'szt.',
    szt: 'szt.',
    kilogram: 'kg',
    kilogramy: 'kg',
    gram: 'g',
    gramy: 'g',
    metr: 'm',
    metry: 'm',
    'metr bieżący': 'mb',
    'metr kwadratowy': 'm²',
    'metr sześcienny': 'm³',
    litr: 'l',
    litry: 'l',
    'mililitr': 'ml',
    'tona': 't',
    opakowanie: 'op.',
    opak: 'op.',
    paleta: 'pal.',
    karton: 'kart.',
    para: 'para',
    komplet: 'kpl.',
    rolka: 'rol.'
  };
  return map[lower] ?? raw;
};

const ClientOfferProductsContainer: React.FC<IProps> = ({
  offerId,
  selectedVariants,
  onVariantsChange,
  onVariantPositionsResolved,
  readOnly = false
}) => {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const { data, isLoading, isError } = useGetSalesCrmMyOfferPositions(offerId, { limit: 100 });

  const items = data?.items ?? [];

  const variantPositionIds = useMemo(
    () => items.filter(hasVariantOptions).map((p) => p.id),
    [items]
  );

  useEffect(() => {
    if (onVariantPositionsResolved) {
      onVariantPositionsResolved(variantPositionIds);
    }
  }, [variantPositionIds.join(','), onVariantPositionsResolved]);

  const handleVariantSelect = (positionId: number, qtyFrom: number) => {
    if (!onVariantsChange) return;
    onVariantsChange({ ...(selectedVariants ?? {}), [positionId]: qtyFrom });
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (isError) {
    return <div style={{ color: '#b3142d', padding: 16 }}>{t('Nie udało się pobrać pozycji oferty.')}</div>;
  }

  if (items.length === 0) {
    return <div style={{ color: '#4b556a', padding: 16 }}>{t('Oferta nie ma pozycji.')}</div>;
  }

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f5f6fa', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', width: 40 }}></th>
              <th style={{ padding: '10px 12px' }}>{t('Produkt')}</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>{t('Ilość')}</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>{t('Cena netto')}</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>{t('Cena brutto')}</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>{t('Wartość netto')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const expanded = expandedId === p.id;
              const isVariant = hasVariantOptions(p);
              const chosenQtyFrom = isVariant ? selectedVariants?.[p.id] ?? null : null;
              const chosenThreshold =
                isVariant && chosenQtyFrom !== null
                  ? p.priceThresholds?.find((th) => th.qtyFrom === chosenQtyFrom) ?? null
                  : null;

              const effectiveQty = isVariant
                ? chosenThreshold?.qtyFrom ?? null
                : p.quantity ?? 0;
              const effectivePriceNet = isVariant
                ? chosenThreshold?.price ?? null
                : p.priceNetSales;
              const valueNet =
                effectiveQty !== null && effectivePriceNet !== null
                  ? effectivePriceNet * effectiveQty
                  : null;
              const unitShort = shortenUnit(p.unit);
              return (
                <React.Fragment key={p.id}>
                  <tr style={{ borderBottom: '1px solid #eef0f4', cursor: 'pointer' }}
                      onClick={() => setExpandedId(expanded ? null : p.id)}>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ color: '#6b54ff' }}>{expanded ? '▾' : '▸'}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <ProductThumb url={p.imageUrl} alt={p.productName ?? ''} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{p.productName ?? `#${p.productId}`}</div>
                          {p.symbol && (
                            <div style={{ fontSize: 12, color: '#4b556a' }}>{p.symbol}</div>
                          )}
                          {isVariant && (
                            <div
                              style={{
                                marginTop: 4,
                                display: 'inline-block',
                                background: '#fdf4e7',
                                color: '#7a5810',
                                fontSize: 10,
                                fontWeight: 700,
                                padding: '1px 6px',
                                borderRadius: 8,
                                letterSpacing: 0.4,
                                textTransform: 'uppercase'
                              }}>
                              {t('Wybierz wariant')}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {effectiveQty === null ? (
                        <span style={{ color: '#9aa1ad', fontSize: 12 }}>{t('Wybierz')}</span>
                      ) : (
                        <>
                          <span style={{ fontWeight: 600 }}>{effectiveQty}</span>{' '}
                          <span style={{ color: '#4b556a', fontSize: 12 }}>{unitShort}</span>
                        </>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      {effectivePriceNet === null ? (
                        <span style={{ color: '#9aa1ad', fontSize: 12 }}>-</span>
                      ) : (
                        <PriceWithBase
                          salesPrice={effectivePriceNet}
                          basePrice={p.priceNetCatalog}
                        />
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      {isVariant ? (
                        <span style={{ color: '#9aa1ad', fontSize: 12 }}>-</span>
                      ) : (
                        <PriceWithBase
                          salesPrice={p.priceGrossSales}
                          basePrice={p.priceGrossCatalog}
                        />
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>
                      {valueNet === null ? (
                        <span style={{ color: '#9aa1ad', fontWeight: 400 }}>-</span>
                      ) : (
                        formatPrice(valueNet)
                      )}
                    </td>
                  </tr>
                  {isVariant && (
                    <tr style={{ background: '#fefaf3', borderBottom: '1px solid #eef0f4' }}>
                      <td></td>
                      <td colSpan={5} style={{ padding: '12px 16px' }}>
                        <VariantPicker
                          thresholds={p.priceThresholds ?? []}
                          unit={unitShort}
                          selectedQtyFrom={chosenQtyFrom}
                          disabled={readOnly}
                          onSelect={(qtyFrom) => handleVariantSelect(p.id, qtyFrom)}
                        />
                      </td>
                    </tr>
                  )}
                  {expanded && (
                    <tr style={{ background: '#fafbfc' }}>
                      <td colSpan={6} style={{ padding: 16 }}>
                        <ClientPositionCommentsCard offerId={offerId} positionId={p.id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {data?.totals && (
            <tfoot>
              <tr style={{ background: '#f5f6fa', fontWeight: 600 }}>
                <td colSpan={5} style={{ padding: '10px 12px', textAlign: 'right' }}>
                  {t('Razem')}:
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  {(() => {
                    const localTotal = computeLocalTotalNet(items, selectedVariants);
                    if (localTotal !== null) return formatPrice(localTotal);
                    return formatPrice(data.totals.valueNet);
                  })()}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

/**
 * Cena sprzedaży + przekreślona cena bazowa + badge zniżki - gdy są różne.
 * Pokazujemy "ile klient zaoszczędził" w stosunku do ceny katalogowej.
 */
const PriceWithBase: React.FC<{
  salesPrice: number | null;
  basePrice: number | null;
}> = ({ salesPrice, basePrice }) => {
  const sales = salesPrice ?? 0;
  const base = basePrice ?? 0;
  const hasDiscount = base > 0 && sales > 0 && sales < base;
  const discountPct = hasDiscount ? Math.round(((base - sales) / base) * 100) : 0;

  if (!hasDiscount) {
    return <span>{formatPrice(salesPrice)}</span>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
        <span
          style={{
            fontSize: 11,
            color: '#9aa1ad',
            textDecoration: 'line-through'
          }}>
          {formatPrice(basePrice)}
        </span>
        <span
          style={{
            background: '#e2f4ea',
            color: '#1f7f48',
            fontSize: 10,
            fontWeight: 700,
            padding: '1px 6px',
            borderRadius: 8
          }}>
          -{discountPct}%
        </span>
      </div>
      <span style={{ fontWeight: 600, color: '#1f7f48' }}>{formatPrice(salesPrice)}</span>
    </div>
  );
};

const VariantPicker: React.FC<{
  thresholds: { qtyFrom: number; price: number }[];
  unit: string;
  selectedQtyFrom: number | null;
  disabled: boolean;
  onSelect: (qtyFrom: number) => void;
}> = ({ thresholds, unit, selectedQtyFrom, disabled, onSelect }) => {
  const { t } = useTranslation();
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#4b556a', marginBottom: 8 }}>
        {t('Dostępne warianty - wybierz jeden, aby zaakceptować ofertę')}
      </div>
      <RadioGroup
        value={selectedQtyFrom !== null ? String(selectedQtyFrom) : ''}
        onChange={(e) => onSelect(Number(e.target.value))}
        sx={{ gap: 4 }}>
        {thresholds.map((th) => {
          const isChecked = selectedQtyFrom === th.qtyFrom;
          return (
            <FormControlLabel
              key={th.qtyFrom}
              value={String(th.qtyFrom)}
              disabled={disabled}
              control={<Radio size="small" sx={{ color: '#6b54ff', '&.Mui-checked': { color: '#6b54ff' } }} />}
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>
                    {th.qtyFrom} {unit}
                  </span>
                  <span style={{ color: '#4b556a' }}>·</span>
                  <span style={{ color: isChecked ? '#1f7f48' : '#1a1d29', fontWeight: isChecked ? 700 : 500 }}>
                    {formatPrice(th.price)} / {unit || t('szt.')}
                  </span>
                  <span style={{ color: '#4b556a', fontSize: 12 }}>
                    ({t('wartość')}: {formatPrice(th.qtyFrom * th.price)})
                  </span>
                </span>
              }
              sx={{
                margin: 0,
                padding: '4px 8px',
                borderRadius: 1,
                background: isChecked ? '#f0ebff' : 'transparent',
                '&:hover': { background: '#f5f6fa' }
              }}
            />
          );
        })}
      </RadioGroup>
    </div>
  );
};

const computeLocalTotalNet = (
  items: ISalesCrmMyOfferPosition[],
  selectedVariants: Record<number, number> | undefined
): number | null => {
  let total = 0;
  for (const p of items) {
    if (hasVariantOptions(p)) {
      const chosen = selectedVariants?.[p.id];
      if (chosen == null) return null;
      const th = p.priceThresholds?.find((x) => x.qtyFrom === chosen);
      if (!th) return null;
      total += th.qtyFrom * th.price;
    } else {
      total += (p.priceNetSales ?? 0) * (p.quantity ?? 0);
    }
  }
  return total;
};

const ProductThumb: React.FC<{ url: string | null; alt: string }> = ({ url, alt }) => {
  const [errored, setErrored] = useState(false);
  const showImage = url && !errored;
  return (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 6,
        border: '1px solid #eef0f4',
        background: '#f5f6fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden'
      }}>
      {showImage ? (
        <img
          src={url ?? ''}
          alt={alt}
          onError={() => setErrored(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <ImageNotSupportedIcon style={{ color: '#c4cad6', fontSize: 22 }} />
      )}
    </div>
  );
};

export default ClientOfferProductsContainer;
